import React from 'react';

import classNames from 'classnames';

import { OneInchToken } from '@api';

import BaseInfoTooltip from '@shared/common/components/BaseInfoTooltip';
import KarmaTag from '@shared/common/components/KarmaTag';

// Constants
const KARMA_MULTIPLIER_TOKEN_TO_SQR = 0.25;
const KARMA_MULTIPLIER_TOKEN_TO_TOKEN = 0.1;
const MAX_REWARD_TOKEN_TO_SQR = 5000;
const MAX_REWARD_TOKEN_TO_TOKEN = 2500;
const MAX_DAILY_REWARD = MAX_REWARD_TOKEN_TO_SQR + MAX_REWARD_TOKEN_TO_TOKEN;

// Helper Component
const SwapKarmaRewardTooltipContent = () => (
  <>
    <ul className="list-disc pl-5 mt-3 flex flex-col gap-2">
      <li>
        Swap to <b>SQR</b>: <br />
        Earn <b>{KARMA_MULTIPLIER_TOKEN_TO_SQR}</b> Karma/USD, max <b>{MAX_REWARD_TOKEN_TO_SQR}</b>{' '}
        Karma daily
      </li>
      <li>
        Swap any tokens: <br />
        Earn <b>{KARMA_MULTIPLIER_TOKEN_TO_TOKEN}</b> Karma/USD, max{' '}
        <b>{MAX_REWARD_TOKEN_TO_TOKEN}</b> Karma daily
      </li>
      <li>
        Swap from <b>SQR</b>: <br />
        No Karma points
      </li>
    </ul>
    <p className="mt-4">
      Total Daily cap: <strong>{MAX_DAILY_REWARD}</strong>
    </p>
    <p className="mt-4">
      Karma may take up to 30 minutes to appear in your MagicID after completing a swap.
    </p>
  </>
);

// Main Component
export interface SwapKarmaRewardProps {
  formattedToTokenPriceInUSD: number;
  isLoadingToTokenPriceInUSD: boolean;
  isLoadingQuote: boolean;
  balanceExceeded: boolean;
  fromToken?: OneInchToken;
  toToken?: OneInchToken;
}

const SwapKarmaRewardWidget = ({
  formattedToTokenPriceInUSD,
  isLoadingToTokenPriceInUSD,
  isLoadingQuote,
  fromToken,
  toToken,
  balanceExceeded,
}: SwapKarmaRewardProps) => {
  // Function to calculate the karma reward amount
  const getKarmaRewardAmount = () => {
    const isSellingSQR = fromToken?.attributes?.symbol === 'SQR';
    const isBuyingSQR = toToken?.attributes?.symbol === 'SQR';
    const isTokenToToken =
      fromToken?.attributes?.symbol !== 'SQR' && toToken?.attributes?.symbol !== 'SQR';

    if (isSellingSQR) {
      return null; // No karma for selling SQR
    }

    let potentialReward = 0;

    if (isBuyingSQR) {
      potentialReward = formattedToTokenPriceInUSD * KARMA_MULTIPLIER_TOKEN_TO_SQR;
    } else if (isTokenToToken) {
      potentialReward = formattedToTokenPriceInUSD * KARMA_MULTIPLIER_TOKEN_TO_TOKEN;
    }

    // Apply individual swap caps without considering the daily accumulated rewards
    if (isBuyingSQR) {
      potentialReward = Math.min(potentialReward, MAX_REWARD_TOKEN_TO_SQR);
    } else if (isTokenToToken) {
      potentialReward = Math.min(potentialReward, MAX_REWARD_TOKEN_TO_TOKEN);
    }

    return potentialReward > 0 ? potentialReward.toFixed(0) : null;
  };

  // Calculate karma reward amount
  const karmaRewardAmount = getKarmaRewardAmount();

  // Determine if karma reward amount should be shown
  const showKarmaRewardAmount = Boolean(
    karmaRewardAmount &&
      karmaRewardAmount !== '0' &&
      !isLoadingToTokenPriceInUSD &&
      !isLoadingQuote,
  );

  if (!showKarmaRewardAmount) {
    return null;
  }

  return (
    <ul
      className={classNames('w-full flex flex-col gap-4 rounded-2xl bg-secondary-100 p-4 mt-4', {
        'border-red-600': balanceExceeded,
      })}
    >
      <li className="flex max-sm:flex-col max-sm:items-start items-center justify-between gap-2 w-full text-neutral-700 text-[14px]">
        <BaseInfoTooltip
          title="Earn Karma Points by Swapping"
          content={<SwapKarmaRewardTooltipContent />}
        >
          You will receive
        </BaseInfoTooltip>
        <div className="flex gap-1 items-center text-neutral-400">
          <KarmaTag approxSign points={karmaRewardAmount || 0} />
          Karma Points
        </div>
      </li>
    </ul>
  );
};

export default SwapKarmaRewardWidget;
